'use client'

/**
 * TrendMessageCard
 *
 * Web counterpart to the mobile app's TrendComponent. Renders a trending
 * topic (query + region/timeframe) with up to 3 seed news items so a
 * trend handoff from the marketing homepage shows the same rich context
 * in the chat bubble instead of raw [TREND]{…}[/TREND] JSON.
 */

import { TrendingUp } from 'lucide-react'
import { decodeHtmlEntities } from '@/lib/utils/decode-html-entities'

export interface TrendNewsItem {
  title: string
  url?: string
  snippet?: string
  picture?: string
  source?: string
}

export interface TrendCardData {
  query: string
  region?: string
  timeframe?: string
  source?: string
  picture?: string
  pictureSource?: string
  traffic?: number
  newsItems?: TrendNewsItem[]
  description?: string
}

export function TrendMessageCard({ data }: { data: TrendCardData }) {
  const newsItems = (data.newsItems || []).filter((item) => item?.title).slice(0, 3)
  const meta = [
    data.region || 'Global',
    data.timeframe ? data.timeframe.replace(/_/g, ' ') : null
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <div className="not-prose my-2 overflow-hidden rounded-2xl border border-border/50 bg-muted/30">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-2">
          <TrendingUp className="text-primary mt-0.5 size-4 shrink-0" />
          <div className="min-w-0">
            <p className="text-foreground text-sm font-semibold leading-snug">
              {decodeHtmlEntities(data.query)}
            </p>
            {meta ? (
              <p className="text-muted-foreground mt-0.5 text-xs">{meta}</p>
            ) : null}
          </div>
        </div>

        {newsItems.length > 0 ? (
          <div className="flex flex-col gap-2">
            {newsItems.map((item, idx) => {
              const title = decodeHtmlEntities(item.title)
              const inner = (
                <div className="flex items-center gap-3 rounded-xl bg-background/60 px-3 py-2">
                  {item.picture ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.picture}
                      alt=""
                      className="size-10 shrink-0 rounded-lg object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
                      <TrendingUp className="text-primary/70 size-4" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground line-clamp-2 text-[13px] font-semibold leading-snug">
                      {title}
                    </p>
                    {item.source ? (
                      <p className="text-muted-foreground mt-0.5 truncate text-[11px]">
                        {decodeHtmlEntities(item.source)}
                      </p>
                    ) : null}
                  </div>
                </div>
              )

              return item.url ? (
                <a
                  key={`${item.url}:${idx}`}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transition-colors hover:opacity-90"
                >
                  {inner}
                </a>
              ) : (
                <div key={`trend-news-${idx}`}>{inner}</div>
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}
